<template>
  <div class="vouchers-list-item">
    <div
      :class="[
        'vouchers-list-item__item-wrapper',
        isRolloutShown && 'vouchers-list-item__item-wrapper--rollout-open',
      ]"
    >
      <div class="vouchers-list-item__details">
        <PillBadge
          v-if="isInBeta"
          class="vouchers-list-item__beta-pill"
          translucid
          sober
          soberBlackFlat
        >
          BETA
        </PillBadge>
        <div class="vouchers-list-item__title">
          {{ voucher.name }}
        </div>
        <p
          v-if="currentStatus === 'Start'"
          :class="[
            'vouchers-list-item__status vouchers-list-item__status',
            !startAvailable && 'vouchers-list-item__status--not-available',
          ]"
        >
          <AlertIcon
            v-if="!startAvailable"
            class="vouchers-list-item__status--not-available__icon"
          />
          <span v-if="!startAvailable">
            The exam will be available on
            <span
              v-tooltip="{
                content: `${availableDateTime} ${localTimeZone}`,
                placement: 'top-center',
              }"
            >
              {{ availableOnDate }}</span
            >.
          </span>
          <span
            v-if="startAvailable && isExternal"
            class="vouchers-list-item__status-current"
            >Expires:
          </span>
          <span v-if="startAvailable">{{ statusText }}</span>
          <span
            v-if="refreshVoucherInterval"
            v-tooltip="{
              content: `on date processing`,
              placement: 'top-center',
            }"
            class="dot-flashing vouchers-list-item__status-dots"
          />
          <span
            class="vouchers-list-item__status-cta"
            @click="handleToggleClick"
          >
            {{ isRolloutShown ? 'Hide history' : 'Show history' }}
          </span>
        </p>
        <p
          v-else
          class="vouchers-list-item__status"
          :style="`--status-gradient: ${statusGradient}`"
        >
          <span
            v-if="currentStatus !== 'Shop'"
            :class="[
              'vouchers-list-item__status-current',
              isExpired && 'vouchers-list-item__status-expired',
            ]"
            ><AlertCircleIcon v-if="isExpired" />{{
              isInProgress ? 'This exam is in progress.' : currentStatus
            }}{{ isInReview ? ':' : '' }}</span
          >{{ statusText }}

          <span
            v-if="refreshVoucherInterval"
            v-tooltip="{
              content: `on date processing`,
              placement: 'top-center',
            }"
            class="dot-flashing vouchers-list-item__status-dots"
          />

          <span
            class="vouchers-list-item__status-cta"
            @click="handleToggleClick"
          >
            {{ isRolloutShown ? 'Hide history' : 'Show history' }}
          </span>
        </p>
      </div>
      <div class="vouchers-list-item__btn-wrapper">
        <span
          v-if="showExtendButton"
          v-tooltip="{
            content: !isExtendable ? extendableTooltip : null,
            placement: 'top-center',
            classes: 'max-width__tooltip',
          }"
          :class="{ 'vouchers-list-item__btn-extend--disabled': !isExtendable }"
        >
          <button
            :class="['btn btn--secondary vouchers-list-item__btn-wrapper-cta']"
            :disabled="!isExtendable || refreshVoucherInterval"
            @click.stop="openModal"
          >
            Extend Voucher
          </button>
        </span>
        <router-link
          v-if="hasPassed"
          :to="{
            name: 'certificate-verified',
            params: { attemptId: passingAttempt.id },
          }"
          class="btn btn--secondary vouchers-list-item__btn-wrapper-cta"
          @click.stop
        >
          Go to certification
        </router-link>
        <button
          v-if="showOpenExamButton"
          :class="OpenExamButtonClasses"
          @click="goToExam"
        >
          {{ OpenExamButtonText }}
        </button>
        <a
          v-if="isExternal"
          :href="`//${elsExamSite}/exams`"
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--primary certification-tile__button vouchers-list-item__btn-wrapper-cta btn--default-icon-color"
        >
          <ExternalLinkIcon /> Go to exam
        </a>
        <a
          v-if="currentStatus === 'Shop'"
          :href="`//${certificationRedirectURL(voucher.id)}`"
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--primary certification-tile__button vouchers-list-item__btn-wrapper-cta"
        >
          Get vouchers
        </a>
        <a
          v-if="currentStatus === 'Beta' && !isRetakeBtnShown"
          :href="
            `${currentVersion.beta_link ||
              `//${certificationRedirectURL(certId, isInBeta)}`}`
          "
          target="_blank"
          rel="noopener nofollow"
          class="btn btn--secondary certification-tile__button vouchers-list-item__btn-wrapper-cta"
        >
          Learn more
        </a>
      </div>
    </div>
    <transition-group name="expand">
      <template v-if="isRolloutShown">
        <VouchersListItemRollout
          v-for="current in activeVouchers"
          :key="current.id"
          :voucher="current"
          :isActive="true"
        />
      </template>
      <template v-if="isRolloutShown">
        <VouchersListItemRollout
          v-for="current in inactiveVouchers"
          :key="current.id"
          :voucher="current"
          :isActive="false"
        />
      </template>
    </transition-group>
  </div>
</template>

<script>
import CONSTANTS from '@/constants'
import {
  voucherCertificationAvailable,
  voucherCertificationAvailableOnDate,
  voucherCertificationAvailableDateTime,
  attemptInProgress,
} from '@/helpers'
import VouchersListItemRollout from '@/components/user-dashboard/certifications/VouchersListItemRollout.vue'
import {
  localTimezone,
  certificationCommons,
  vouchersCommons,
} from '@/helpers/mixins'
import AlertIcon from '@/assets/icons/alert-circle-10.svg'
import { ExternalLinkIcon, AlertCircleIcon } from 'vue-feather-icons'
import { mapActions } from 'vuex'
import PillBadge from '@/components/shared/PillBadge.vue'

export default {
  name: 'VouchersListItem',
  components: {
    VouchersListItemRollout,
    AlertIcon: { ...AlertIcon, name: 'AlertIcon' },
    ExternalLinkIcon,
    AlertCircleIcon,
    PillBadge,
  },
  mixins: [localTimezone, certificationCommons, vouchersCommons],
  props: {
    voucher: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isRolloutShown: false,
      elsExamSite: process.env.VUE_APP_ELS_EXAM_SITE,
      refreshVoucherInterval: false,
      certification: this.voucher,
    }
  },
  computed: {
    statusGradient() {
      const gradients = {
        Failed: 'linear-gradient(180deg, #E52A2A 0%, #FF5E5E 100%)',
        'Pending Review': '#EEEEEE',
        'In Progress': '#EEEEEE',
        Passed: 'linear-gradient(145.56deg, #00DA99 20.53%, #70FFD4 79.76%)',
        Start: '#EEEEEE',
        Expired: '#999999',
        Shop: '#EEEEEE',
        Beta: '#EEEEEE',
      }
      return gradients[this.currentStatus]
    },
    statusText() {
      const texts = {
        Failed:
          this.retriesAvailable > 0
            ? `You have ${this.retriesAvailable} retake${
                this.retriesAvailable === 1 ? '' : 's'
              } ${
                this.refreshVoucherInterval
                  ? '.'
                  : `available until ${this.retryExpiry}.`
              }`
            : 'You have 0 retakes available.',
        'Pending Review': `We're reviewing your exam. You'll get a notification when the results are in.`,
        Passed: 'Congratulations you passed your exam!',
        'In Progress': 'Click Resume Exam to continue.',
        Start: this.isExternal
          ? `${
              this.refreshVoucherInterval
                ? ''
                : this.formatDate(this.lastVoucherExpirationDate)
            }`
          : this.retriesAvailable > 0
          ? `You have ${this.retriesAvailable} attempt${
              this.retriesAvailable === 1 ? '' : 's'
            } ${
              this.refreshVoucherInterval
                ? '.'
                : `available until ${this.retryExpiry}.`
            }`
          : 'You have 0 attempts available.',
        Expired: this.lastVoucherExpirationDate
          ? `${this.formatDate(this.lastVoucherExpirationDate)}.`
          : 'You have no available vouchers.',
        Shop: 'You have no available vouchers.',
        Beta: 'You have no available vouchers. This is a closed beta.',
      }
      return texts[this.currentStatus]
    },
    isRetakeBtnShown() {
      return (
        !this.hasPassed &&
        !this.isInProgress &&
        !this.isInReview &&
        this.allAttempts.length > 0 &&
        this.retriesAvailable > 0
      )
    },
    startAvailable() {
      return voucherCertificationAvailable(this.activeVouchers)
    },
    availableOnDate() {
      return voucherCertificationAvailableOnDate(this.activeVouchers)
    },
    availableDateTime() {
      return voucherCertificationAvailableDateTime(this.activeVouchers)
    },
    isExternal() {
      return this.voucher.is_external
    },
    showExtendButton() {
      return (
        !this.hasPassed &&
        !this.isInReview &&
        !this.isInBeta &&
        !CONSTANTS.CERTIFICATIONS.CANT_EXTEND.includes(this.voucher.id)
      )
    },
    extendableTooltip() {
      const texts = {
        Expired: 'Expired vouchers cannot be extended',
        'In Progress': 'In progress vouchers cannot be extended',
        Failed: 'In progress vouchers cannot be extended',
      }
      if (this.reachedMaxExtensions) {
        return 'Maximum extensions reached for this voucher'
      }
      return texts[this.currentStatus] ?? 'Cannot be extended'
    },
    certId() {
      return '' // backend should return the cert Id in a more reliable way, right now it's in the attempts level
    },
    showOpenExamButton() {
      return (
        this.isInProgress ||
        this.isRetakeBtnShown ||
        (this.currentStatus === 'Start' && !this.isExternal)
      )
    },
    OpenExamButtonClasses() {
      return [
        'btn',
        'btn--primary',
        'vouchers-list-item__btn-wrapper-cta',
        {
          'vouchers-list-item__start-disabled':
            !this.startAvailable && this.currentStatus === 'Start',
        },
      ]
    },
    OpenExamButtonText() {
      if (this.isRetakeBtnShown) {
        return 'Retake Exam'
      }
      return this.isInProgress ? 'Resume Exam' : 'Start Exam'
    },
  },
  methods: {
    ...mapActions({
      getVoucherById: 'certifications/getVoucherById',
      getCertificationsForLearningPath:
        'certifications/getCertificationsForLearningPath',
      updateCurrentCertification: 'certifications/updateCurrentCertification',
      updateCertificationsCache: 'certifications/updateCertificationsCache',
    }),
    handleToggleClick() {
      if (this.allVouchers.length > 0) {
        this.isRolloutShown = !this.isRolloutShown
      }
      return null
    },
    openModal() {
      if (this.isExtendable) {
        this.refreshVoucherInterval = setInterval(
          this.confirmVoucherUpdate,
          3000
        )
        this.$emit('extend-voucher', {
          isExternal: this.isExternal,
          certificateName: this.voucher.name,
          voucherId: this.lastVoucherId,
          cancelCallback: () => {
            this.refreshVoucherInterval = clearInterval(
              this.refreshVoucherInterval
            )
          },
        })
      }
    },
    async confirmVoucherUpdate() {
      const voucher = await this.getVoucherById(this.lastVoucherId)
      if (this.extensions?.length < voucher.extensions?.length) {
        this.updateVoucherExtensions(voucher)
        this.refreshVoucherInterval = clearInterval(this.refreshVoucherInterval)
      }
    },
    async goToExam() {
      const voucherId = this.lastVoucherId

      if (!voucherId) return

      await Promise.all([
        this.updateCurrentCertification(this.certification),
        this.updateCertificationsCache(this.certification),
      ])

      const attempt = this.lastAttempt
      const attemptId = attemptInProgress(attempt)?.id || null

      this.$router.push({
        name: 'certification-exam',
        params: { voucherId, attemptId },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
* {
  will-change: height;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}
.vouchers-list-item {
  &__item-wrapper {
    padding: 20px;
    background: linear-gradient(
      104.99deg,
      rgba(62, 63, 73, 0.7) -13.91%,
      rgba(62, 63, 73, 0) 100.48%
    );
    border-radius: 10px;
    color: var(--white);
    @include tablet {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      padding: 30px;
    }
    &--rollout-open {
      border-radius: 10px 10px 0px 0px;
    }
    &--clickable {
      cursor: pointer;
      &:hover {
        transition: 0.3s;
        background-color: rgb(255 255 255 / 5%);
      }
    }
  }
  &__title {
    padding: 35px 0 10px 0;
    font-size: rem(16);
    line-height: 23px;
    font-weight: 500;
    @include mobile {
      padding: 20px 0 10px 0;
    }
    @include tablet {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      padding: 0;
    }
  }
  &__status {
    color: var(--light-blue-gray);
    padding: 0;
    position: relative;
    font-size: rem(12px);
    line-height: 14px;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;

    @include mobile {
      flex-direction: row;
    }

    &--not-available {
      display: flex;
      align-items: center;
      &__icon {
        margin-right: 17px;
      }
    }

    &-current {
      color: var(--white);
      font-weight: 700;
      display: flex;
      gap: 5px;
    }

    &-expired {
      color: var(--performance-light);
      svg {
        width: 16px;
        height: 16px;
      }
    }

    &-cta {
      opacity: 0.99;
      color: var(--purple-pale);

      &:hover {
        color: var(--purple-dark);
        cursor: pointer;
      }
    }

    &-dots {
      margin: 4px 10px 0 10px;
      &::before {
        left: -10px;
      }
      &::after {
        left: 10px;
      }
    }
  }
  &__btn {
    &-wrapper {
      margin: 25px 0 auto auto;
      display: flex;
      align-items: center;
      gap: 10px;

      @include tablet {
        margin: auto 0 auto auto;
      }

      &-cta {
        font-size: 14px;
        line-height: 20px;
        padding: 8px 16px;
        line-height: 20px;
        height: fit-content;

        svg {
          width: 16px;
          height: 16px;
        }
      }
    }

    &-extend {
      &--disabled {
        cursor: not-allowed;
      }
    }
  }
  .expand-enter-active,
  .expand-leave-active {
    transition-property: opacity;
    transition: 0.15s ease-in-out;
    overflow: hidden;
  }

  .expand-enter,
  .expand-leave-to {
    opacity: 0;
  }
  &__start-disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  &__timezone-icon {
    font-size: 16px !important;
    margin-left: 5px;
    color: var(--gray);
  }
  &__beta-pill {
    margin-bottom: 5px;
  }
}
</style>
